'use client';
import {
  Tab,
  Box,
  Tabs,
  Text,
  HStack,
  VStack,
  TabList,
  TabPanel,
  TabPanels,
  Accordion,
} from '@chakra-ui/react';
import { useCallback, useState } from 'react';

import SliderControls from 'features/SliderControls';
import { useSwiperControls } from 'shared/hooks';
import AccordionInfo from 'shared/AccordionInfo';
import TechExpertCard from './TechExpertCard';
import BlockHeader from 'shared/BlockHeader';
import ContentParser from './ContentParser';
import ButtonGoTo from 'shared/ButtonGoTo';
import NextImage from 'shared/NextImage';
import Slider from 'shared/Slider';

import DownAngle from 'assets/icons/DownAngle.svg';
import RightAngle from 'assets/icons/RightAngle.svg';
import { techExpertDataSelector } from 'constants/TechExpert';

export default function TechExpert({ type = 'default' }) {
  const {
    title = null,
    titleProps = null,
    RenderTitle = null,
    cardsData,
    hideBelow = 'tablet',
    tabTitleProps = null,
  } = techExpertDataSelector[type];
  const [swiper, setSwiper] = useState(null);
  const [active, setActive] = useState(-1);
  const controls = useSwiperControls(swiper);
  const accordionHandler = useCallback(id => setActive(id), []);

  return (
    <VStack
      as="section"
      w="full"
      align="start"
      gap={{ mobile: '20px', tablet: '24px', laptop: '40px' }}
    >
      {title && <BlockHeader {...titleProps}>{title}</BlockHeader>}

      {RenderTitle}

      <Tabs gap="20px" mt="8px">
        <TabList maxW="calc(50% - 10px)" hideBelow={hideBelow} gap={{ mobile: '0', tablet: '8px' }}>
          {cardsData.map(({ title, tabIcon, gap = null }, i) => (
            <Tab key={i}>
              <HStack w="full" {...gap}>
                <Box
                  boxSize={{ mobile: '36px', laptop: '56px' }}
                  borderRadius="50%"
                  bgColor="bg.neonLight"
                  p={{ mobile: '10px', laptop: '14px' }}
                  flexShrink="0"
                >
                  <NextImage src={tabIcon} alt={title} w="auto" h="auto" className="leftIcon" />
                </Box>

                <Text as="h4" flex="1 1 auto" textAlign="start" {...tabTitleProps}>
                  {title}
                </Text>

                <NextImage
                  src={RightAngle}
                  alt="TabArrowRight"
                  w="auto"
                  h="auto"
                  className="rightIcon"
                />
              </HStack>
            </Tab>
          ))}
        </TabList>

        <TabPanels maxW="calc(50% - 10px)" hideBelow={hideBelow}>
          {cardsData.map((cardData, i) =>
            cardData.contentSliderCardsData ? (
              <TabPanel key={i} pos="relative">
                <Slider
                  controller={swiper}
                  setController={setSwiper}
                  slidesData={cardData.contentSliderCardsData}
                  SlideComponent={TechExpertCard}
                  isBreakpoints={false}
                  pagination={{ type: 'custom' }}
                  mousewheel={false}
                  loop
                  container={{ h: 'full' }}
                />

                <SliderControls
                  hideBelow="tablet"
                  pos="absolute"
                  inset={{ mobile: 'auto 24px 24px auto', laptop: 'auto 48px 48px auto' }}
                  {...controls}
                />
              </TabPanel>
            ) : (
              <TabPanel key={i}>
                <TechExpertCard {...cardData} />
              </TabPanel>
            ),
          )}
        </TabPanels>

        <Accordion
          allowToggle
          flex={1}
          w="full"
          gap="16px"
          display="flex"
          flexDirection="column"
          hideFrom="tablet"
          onChange={accordionHandler}
        >
          {cardsData.map(
            (
              {
                title,
                accordionTitleSx = {
                  fontSize: '18px',
                  fontWeight: 500,
                  lineHeight: '120%',
                  letterSpacing: '-0.03em',
                  ...tabTitleProps,
                },
                content = null,
                contentSliderCardsData = null,
                accordionContentSx = { color: 'text.darkGray', textStyle: 'h4', fontSize: '16px' },
                tabIcon,
                link = null,
                description = null,
              },
              i,
            ) => (
              <AccordionInfo
                key={i}
                title={title}
                titleSx={accordionTitleSx}
                description={description}
                link={link}
                RenderContent={
                  <AccordionContentRender
                    content={content}
                    contentSliderCardsData={contentSliderCardsData}
                    swiper={swiper}
                    setSwiper={setSwiper}
                    sliderControls={controls}
                  />
                }
                contentSx={accordionContentSx}
                leftIcon={
                  <Box
                    borderRadius="50%"
                    bgColor="bg.neonLight"
                    p={{ mobile: '8px', laptop: '20px' }}
                    flexShrink="0"
                  >
                    <NextImage
                      src={tabIcon}
                      alt={title}
                      className="leftIcon"
                      w="auto"
                      h="auto"
                      boxSize="20px"
                    />
                  </Box>
                }
                rightIcon={DownAngle}
                containerStyle={{
                  borderRadius: '24px',
                  gap: '10px',
                  p: '4px 16px 16px 4px',
                  bgColor: active === i && 'bg.grayLight',
                }}
                panelStyle={{
                  textStyle: 'button.secondary',
                  overflow: 'visible',
                  ps: '12px',
                  pt: '12px',
                  pb: '0',
                }}
              />
            ),
          )}
        </Accordion>
      </Tabs>

      <ButtonGoTo size="secondary" w="full" />
    </VStack>
  );
}

const AccordionContentRender = ({
  content = null,
  contentSliderCardsData = null,
  swiper,
  setSwiper,
  sliderControls,
}) => {
  if (content) {
    return <ContentParser content={content} />;
  }

  if (contentSliderCardsData) {
    return (
      <VStack pos="relative" align="end">
        <Slider
          controller={swiper}
          setController={setSwiper}
          slidesData={contentSliderCardsData.map(({ title, ...cardData }) => ({ ...cardData }))}
          SlideComponent={TechExpertCard}
          isBreakpoints={false}
          pagination={{ type: 'custom' }}
          mousewheel={false}
          loop
          container={{ h: 'full' }}
        />

        <SliderControls hideBelow="unset" {...sliderControls} />
      </VStack>
    );
  }
};
